import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import {
  DataTableComponent,
  TableColumn,
  TableConfig,
} from '@components/data-table/data-table.component';
import { Loading } from '@components/ui/loading/loading';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import { HostConfigurationOverlayService } from '@6grain/masofa-monolith-api-client/common/api/hostConfigurationOverlay.service';
import { HostConfigurationOverlayListItemResponse } from '@6grain/masofa-monolith-api-client/common/model/hostConfigurationOverlayListItemResponse';
import { CommandConfigLinksComponent } from './command-config-links';
import { OrchestrationCommandConfigDetailComponent } from './orchestration-command-config-detail';

@Component({
  selector: 'app-orchestration-command-config-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SecondaryButton,
    DataTableComponent,
    Loading,
    OrchestrationTableHeadingComponent,
    CommandConfigLinksComponent,
    DialogModule,
    OrchestrationCommandConfigDetailComponent,
  ],
  templateUrl: './orchestration-command-config-page.html',
  styleUrl: './orchestration-command-config-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationCommandConfigPageComponent implements OnInit {
  private readonly api = inject(HostConfigurationOverlayService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly editorSection = signal<string | null>(null);
  readonly editorOpen = computed(() => !!this.editorSection());
  readonly dialogTitle = computed(() => {
    this.lang();
    const name = this.editorSection();
    const row = this.sections().find(item => item.sectionName === name);
    const label = row?.displayName || name || '';
    return label ? this.translate.instant(label) : '';
  });

  readonly loading = signal(false);
  readonly errorMessage = signal('');
  readonly searchQuery = signal('');
  readonly sections = signal<HostConfigurationOverlayListItemResponse[]>([]);
  private readonly editActionTemplate = viewChild<TemplateRef<unknown>>('editActionTemplate');
  private readonly processesTemplate = viewChild<TemplateRef<unknown>>('processesTemplate');
  private readonly translatedCell = viewChild<TemplateRef<unknown>>('translatedCell');
  private readonly translate = inject(TranslateService);
  private readonly lang = signal(this.translate.currentLang);

  readonly visibleSections = computed(() =>
    filterByCatalogQuery(this.sections(), this.searchQuery(), item => [
      item.sectionName ?? '',
      item.displayName ?? '',
      item.description ?? '',
      item.source ?? '',
      ...(item.consumers ?? []),
    ]),
  );

  readonly columns = computed<TableColumn[]>(() => {
    this.lang();
    const translated = this.translatedCell();
    return [
    { field: 'displayName', header: 'Command configuration', type: 'custom', customTemplate: translated },
    { field: 'sectionName', header: 'Section', type: 'text' },
    { field: 'description', header: 'Description', type: 'custom', customTemplate: translated },
    {
      field: 'processes',
      header: 'Processes',
      type: 'custom',
      customTemplate: this.processesTemplate(),
    },
    { field: 'source', header: 'Source', type: 'custom', customTemplate: translated },
    { field: 'restartRequired', header: 'Restart required', type: 'boolean' },
    {
      field: 'consumers',
      header: 'Consumers',
      type: 'text',
      parseFunction: (row: HostConfigurationOverlayListItemResponse) =>
        (row.consumers ?? []).join(', '),
    },
    {
      field: 'edit',
      header: '',
      type: 'custom',
      width: '9rem',
      customTemplate: this.editActionTemplate(),
    },
  ];
  });

  readonly tableConfig = computed<TableConfig>(() => ({
    paginator: false,
    emptyMessage: 'No command configuration sections',
  }));

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.editorSection.set(params.get('sectionName'));
    });
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      this.lang.set(event.lang);
    });
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.api
      .orchestrationCommandConfigurationGet()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: items => {
          this.sections.set(items ?? []);
          this.loading.set(false);
        },
        error: () => {
          this.errorMessage.set('Failed to load command configuration');
          this.loading.set(false);
        },
      });
  }

  onCatalogSearch(query: string): void {
    this.searchQuery.set(query ?? '');
  }

  selectSection(row: HostConfigurationOverlayListItemResponse): void {
    const sectionName = row.sectionName;
    if (!sectionName) {
      return;
    }
    void this.router.navigate(['/admin/process-management/command-config', sectionName]);
  }

  onEditorVisible(open: boolean): void {
    if (!open) {
      void this.router.navigate(['/admin/process-management/command-config']);
    }
  }
}
